import { IExtractedService, ISuggestedImage, IService, ServiceType } from '@torim-app/types';
import { adminUrlBuilder } from '@app/api/utils';

export class ServicesApi {
  static readonly path = 'services';

  /** Fetch suggested stock images for a service name (the picker gallery). */
  static async getSuggestedImages(query: string): Promise<ISuggestedImage[]> {
    const url = adminUrlBuilder({ query }, `${ServicesApi.path}/suggested-images`);
    const response = await fetch(url, { method: 'get' });
    if (!response.ok)
      throw 'Failed to fetch suggested images';

    const data = await response.json();
    return data.images ?? [];
  }

  /** Re-upload a chosen suggested image to ImageKit; returns the permanent URL to save. */
  static async saveSuggestedImage(imageUrl: string): Promise<string> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/save-image`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ imageUrl })
    });
    if (!response.ok)
      throw 'Failed to save image';

    const data = await response.json();
    return data.imageUrl;
  }

  /** Extract a services list from uploaded images / PDFs using AI (multimodal). */
  static async extractServicesFromFiles(body: FormData): Promise<IExtractedService[]> {
    const url = adminUrlBuilder({}, 'ai/extract-services');
    const response = await fetch(url, {
      method: 'post',
      body
    });
    if (!response.ok)
      throw 'Failed to extract services from files';

    const data = await response.json();
    return data.services ?? [];
  }

  /** Import extracted services. Existing services (by name) are skipped, never overridden. */
  static async importServices(
    services: IExtractedService[],
    providingClients?: string[]
  ): Promise<{ created: number; skipped: number; services: IService[] }> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/import`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ services, providingClients })
    });
    if (!response.ok)
      throw 'Failed to import services';

    return await response.json();
  }

  /** Convert a service to a different type (e.g. regular -> complementary). */
  static async convertServiceType(
    serviceId: string,
    serviceType: ServiceType,
    attachedToServices?: string[]
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/convert-type`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ serviceId, serviceType, attachedToServices })
    });
    if (!response.ok)
      throw 'Failed to convert service type';
  }

  static async fetchAllServices(): Promise<IService[]> {
    const url = adminUrlBuilder({}, ServicesApi.path);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get all services';

    return await response.json();
  }

  static async addService(
    body: IService
  ): Promise<IService[]> {
    const url = adminUrlBuilder({}, ServicesApi.path);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to create new service`;

    return await response.json();
  }

  static async updateService(
    body: IService
  ) {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/update`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to update service`;
  }

  static async changeServiceVisibility(
    sid: string,
    visibility: string
  ): Promise<void> {
    const url = adminUrlBuilder({ sid, visibility }, ServicesApi.path);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw `Failed to create new manual event`;
  }

  static async attachServicesToProvider(
    body: { servicesIds: string[]; providerId: string }
  ) {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/update/provider`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to attach services to provider`;
  }

  static async setProviderOverride(
    body: { serviceId: string; providerId: string; price?: number; maxPrice?: number; duration?: number }
  ): Promise<IService[]> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/update/provider-override`);
    const response = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to set provider override`;
    return response.json();
  }

  static async reorderServices(serviceIds: string[]): Promise<void> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/reorder`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ serviceIds })
    });
    if (!response.ok)
      throw 'Failed to reorder services';
  }

  static async getGroupedServices(): Promise<{
    regular: Array<IService>;
    complementary: Array<IService>;
    hidden: Array<IService>;
  }> {
    const url = adminUrlBuilder({}, `${ServicesApi.path}/grouped`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw `Failed to get grouped services`;

    return await response.json();

  }
}
